import { Check, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/site/motion";
import { motion } from "framer-motion";

const TIERS = [
  {
    key: "tractor",
    name: "Tractor Emulsion",
    desc: "Economy finish · 2 coats paint",
    badge: "Value",
  },
  {
    key: "premium",
    name: "Premium Emulsion",
    desc: "Matte/satin · 2 coats of paint",
    badge: "Most popular",
    featured: true,
  },
  {
    key: "royal",
    name: "Royale Emulsion",
    desc: "Luxury wash finish · 2 coats",
    badge: "Best finish",
  },
];

// Pricing matrix in INR (full job, all-inclusive labour + paint)
export const BHK_PRICES = {
  "1": { tractor: 20000, premium: 25000, royal: 30000 },
  "2": { tractor: 25000, premium: 30000, royal: 35000 },
  "3": { tractor: 30000, premium: 35000, royal: 40000 },
};

const BHKS = ["1", "2", "3"];

const inr = (n) =>
  "₹" + new Intl.NumberFormat("en-IN").format(n);

const inrK = (n) => `₹${n / 1000}K`;

export default function RateCard({ onGetQuote }) {
  return (
    <section id="rates" data-testid="rates-section" className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div className="max-w-2xl">
            <Reveal className="text-xs font-bold tracking-[0.22em] uppercase text-orange-600">
              Transparent pricing
            </Reveal>
            <Reveal as="h2" delay={0.05} className="mt-3 font-display font-black text-3xl sm:text-4xl lg:text-5xl text-ink">
              Flat prices by home size &amp; paint.
            </Reveal>
            <Reveal delay={0.1} className="mt-4 text-ink-2">
              No surprises. Pick your apartment size and paint type — the price is
              all-inclusive (labour, paint, primer &amp; clean-up).
            </Reveal>
          </div>
          <Reveal delay={0.15} className="inline-flex items-center gap-2 rounded-full bg-orange-50 px-4 py-2 text-sm font-bold text-orange-700">
            <TrendingUp className="h-4 w-4" /> Save up to 25% this season
          </Reveal>
        </div>

        {/* Tier cards — 3 columns showing each paint type with prices per BHK */}
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
          {TIERS.map((t, i) => (
            <motion.article
              key={t.key}
              data-testid={`rate-card-${i}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.8, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              whileHover={{ y: -6 }}
              className={`relative rounded-2xl p-7 transition-shadow duration-300 ${
                t.featured
                  ? "bg-ink text-white shadow-[0_20px_60px_rgba(15,23,42,0.25)]"
                  : "bg-white hairline shadow-soft hover:shadow-[0_18px_50px_rgba(15,23,42,0.08)]"
              }`}
            >
              <div className="absolute -top-3 left-6 rounded-full bg-orange-500 text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1">
                {t.badge}
              </div>
              <h3 className={`mt-2 font-display font-bold text-lg ${t.featured ? "text-white" : "text-ink"}`}>
                {t.name}
              </h3>
              <p className={`mt-1 text-sm ${t.featured ? "text-white/70" : "text-muted-ink"}`}>
                {t.desc}
              </p>

              <ul className="mt-6 space-y-3">
                {BHKS.map((bhk) => (
                  <li key={bhk} className="flex items-center justify-between">
                    <span className={`text-sm font-bold ${t.featured ? "text-white/80" : "text-ink-2"}`}>
                      {bhk} BHK
                    </span>
                    <span className={`font-display font-extrabold text-xl tracking-tight ${t.featured ? "text-white" : "text-ink"}`}>
                      {inr(BHK_PRICES[bhk][t.key])}
                    </span>
                  </li>
                ))}
              </ul>

              <div className={`mt-5 pt-5 border-t ${t.featured ? "border-white/10" : "border-slate-100"}`}>
                <ul className="space-y-2">
                  {[
                    "Branded Asian Paints",
                    "2 coats paint",
                    t.key === "royal" ? "Lifetime wash finish" : t.key === "premium" ? "Low-VOC, matte/satin" : "Quick refresh in 2–3 days",
                  ].map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm">
                      <Check className={`h-4 w-4 mt-0.5 ${t.featured ? "text-orange-300" : "text-orange-600"}`} />
                      <span className={t.featured ? "text-white/80" : "text-ink-2"}>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <Button
                onClick={onGetQuote}
                data-testid={`rate-card-cta-${i}`}
                className={`mt-7 w-full rounded-full font-bold ${
                  t.featured
                    ? "bg-orange-500 hover:bg-orange-600 text-white"
                    : "bg-ink hover:bg-slate-800 text-white"
                }`}
              >
                Get Quote
              </Button>
            </motion.article>
          ))}
        </div>

        {/* Compact reference matrix */}
        <div className="mt-8 rounded-2xl bg-orange-50/40 hairline p-5 md:p-6 overflow-x-auto" data-testid="rate-matrix">
          <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-orange-700">
            Quick reference matrix
          </div>
          <table className="mt-3 w-full min-w-[480px] text-left text-sm">
            <thead>
              <tr className="text-muted-ink">
                <th className="py-2 font-bold">Size</th>
                {TIERS.map((t) => (
                  <th key={t.key} className="py-2 font-bold text-center">{t.name.replace(" Emulsion", "")}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {BHKS.map((bhk) => (
                <tr key={bhk} className="border-t border-orange-100">
                  <td className="py-3 font-bold text-ink">{bhk} BHK</td>
                  {TIERS.map((t) => (
                    <td key={t.key} className="py-3 text-center font-display font-extrabold text-ink">
                      {inrK(BHK_PRICES[bhk][t.key])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="mt-6 text-xs text-muted-ink">
          * Prices are all-inclusive of labour and branded paints for a standard repaint.
          Designer textures, waterproofing &amp; structural repairs may vary after site visit.
        </p>
      </div>
    </section>
  );
}
